import React, { useContext, useState } from "react";
import { Modal } from "@mui/material";
import { Visibility as PreviewIcon } from "@mui/icons-material";
import {
    ButtonRow,
    Expandable,
    InfoText,
    ModalWrapper,
    StyledBackdrop,
    TitleText,
    TitleWrapper,
} from "../../../MaterialOrder/single/AddMaterialModal/Styled";
import { StyledButton } from "../../../MaterialOrder/Styled";
import { ExportProductForm } from "./ExportProductForm";
import { ExportProductList } from "./ExportProductList";
import { api } from "../../../../shared/hooks/api";
import { error, success } from "../../../../utils/toast";
import useMergeState from "../../../../shared/hooks/useMergeState";
import { ROLE, STORAGE } from "../../../../data/enums";
import { AuthenticationContext } from "../../../../provider/AuthenticationProvider";
import LoadingButton from "../../../../shared/components/LoadingButton";
import DocxPreviewModal from "../../../../shared/components/DocxPreviewModal";
import { generateDocumentBlob } from "../../../../utils/exportUtils";
import { buildDocxData } from "../../../../utils/docxBuilder";
import { productExportPreviewDescriptor } from "../../../../utils/docxMappings";
import ExportTemplate from "../../../../templates/PMS - Phiếu xuất kho thành phẩm.docx";


interface PrefillProductItem {
    id: string;
    name: string;
    exportQuantity: number;
    note?: string;
}

interface ExportProductModalProps {
    open: boolean;
    handleClose: () => void;
    products?: any[];
    initialFormData?: Partial<ReturnType<typeof initialData> & { customer?: any; export_date?: string }>;
    initialItems?: PrefillProductItem[];
    onExported?: () => void;
    deliveryTripId?: string;
    onAfterExport?: () => Promise<void>;
}

export const getStorageForRole = (role: ROLE): string => {
    if (role === ROLE.FIRST_STORAGE_KEEPER) {
        return STORAGE.FIRST;
    } else if (role === ROLE.SECOND_STORAGE_KEEPER) {
        return STORAGE.SECOND;
    }
    return "";
};

const initialData = (role: ROLE) => {
    return {
        receiver_name: "",
        address: "",
        customer: null,
        reason: "",
        storage: getStorageForRole(role),
        note: "",
        products: [],
    };
};

export const ExportProductModal = ({
                                       open,
                                       handleClose,
                                       products,
                                       initialFormData,
                                       initialItems,
                                       onExported,
                                       deliveryTripId,
                                       onAfterExport,
                                   }: ExportProductModalProps) => {
    // Since this component is mounted only when open=true (caller renders conditionally),
    // initialize internal states from props so prefill is ready on first paint.
    const { user: { role } } = useContext(AuthenticationContext);
    const [isOpen, setIsOpen] = useState(true);
    const [formData, mergeFormData] = useMergeState({
        ...initialData(role),
        ...(initialFormData || {}),
    });
    const [selectedProducts, mergeSelectedProducts] = useMergeState(
        Array.isArray(initialItems) ? (initialItems as any) : [],
    );
    const [{ isCreating }, createExport] = api.post("/products/export/");
    const [isPreviewOpen, setIsPreviewOpen] = useState(false);
    const [previewBlob, setPreviewBlob] = useState<Blob | null>(null);
    const [hasExported, setHasExported] = useState(false);

    const handleCloseWithAnimation = () => {
        setIsOpen(false);
        setTimeout(handleClose, 200);
    };

    const handleExport = async () => {
        try {
            const customerValue = (formData as any).customer && typeof (formData as any).customer === "object"
                ? (formData as any).customer.name
                : (formData as any).customer;
            const exportData = {
                ...formData,
                customer: customerValue,
                products: selectedProducts.map((item: any) => ({
                    product: item.id,
                    quantity: item.exportQuantity,
                    note: item.note || "",
                })),
            };
            await createExport(exportData);

            if (onAfterExport) {
                try {
                    await onAfterExport();
                } catch (headerErr) {
                    console.error("Failed to update delivery trip header:", headerErr);
                    error("Cảnh báo", "Xuất kho thành công nhưng không thể cập nhật trạng thái chuyến giao hàng");
                }
            }

            success("THÀNH CÔNG", "Xuất kho sản phẩm thành công");
            setHasExported(true);
            setIsPreviewOpen(false);
            setPreviewBlob(null);
            onExported && onExported();
            handleCloseWithAnimation();
        } catch (err) {
            error("Không thể xuất kho sản phẩm", err);
        }
    };

    const validateExportData = () => {
        const errors: string[] = [];
        if (!formData.storage) errors.push("Kho xuất không được để trống.");
        if (!formData.customer) errors.push("Khách hàng không được để trống.");
        if (!formData.reason) errors.push("Lý do xuất không được để trống.");
        if (!formData.export_date) errors.push("Ngày xuất kho không hợp lệ.");
        if (!selectedProducts || (selectedProducts as any[]).length === 0) errors.push("Danh sách sản phẩm không được để trống.");
        return { isValid: errors.length === 0, errors };
    };

    const handlePreview = async () => {
        const validation = validateExportData();
        if (!validation.isValid) {
            alert("Vui lòng điền đầy đủ thông tin trước khi xem trước:\n\n" + validation.errors.join("\n\n"));
            return;
        }
        try {
            const data = buildDocxData(
                { formData, selectedProducts },
                productExportPreviewDescriptor,
            );
            const blob = await generateDocumentBlob(data, ExportTemplate);
            setPreviewBlob(blob);
            setIsPreviewOpen(true);
        } catch (e) {
            console.error("Failed to generate product export preview DOCX:", e);
            alert("Không thể tạo bản xem trước tài liệu.");
        }
    };

    console.log("formData: ", formData);

    return (
        <>
            <Modal
                open={open}
                onClose={handleCloseWithAnimation}
                closeAfterTransition
                BackdropComponent={StyledBackdrop}
                BackdropProps={{
                    timeout: 500,
                    onClick: (e) => e.stopPropagation(),
                }}
            >
                <ModalWrapper isOpen={isOpen}>
                    <Expandable>
                        <TitleWrapper>
                            <TitleText>Xuất kho sản phẩm</TitleText>
                            <InfoText>Vui lòng điền thông tin xuất kho và chọn sản phẩm</InfoText>
                        </TitleWrapper>

                        <ExportProductForm formData={formData} onChange={mergeFormData} />

                        <TitleText>Danh sách xuất kho sản phẩm</TitleText>

                        <ExportProductList
                            selectedProducts={selectedProducts}
                            setSelectedProducts={mergeSelectedProducts}
                            exportInfo={formData}
                            disableQuantityInput={Boolean(deliveryTripId) && [ROLE.FIRST_STORAGE_KEEPER, ROLE.SECOND_STORAGE_KEEPER].includes(role)}
                        />
                    </Expandable>

                    <ButtonRow>
                        <StyledButton variant="outlined" onClick={handleCloseWithAnimation}>Huỷ bỏ</StyledButton>
                        <StyledButton onClick={handlePreview} startIcon={<PreviewIcon />} variant="contained"
                                      disabled={hasExported}>Xem trước phiếu</StyledButton>
                    </ButtonRow>
                </ModalWrapper>
            </Modal>
            <DocxPreviewModal
                open={isPreviewOpen}
                onClose={() => setIsPreviewOpen(false)}
                blob={previewBlob}
                title="Xem trước phiếu xuất kho thành phẩm"
                BackdropComponent={StyledBackdrop}
                backdropProps={{ timeout: 300 }}
                debug
                actions={
                    <LoadingButton
                        variant="contained"
                        onClick={handleExport}
                        disabled={isCreating || hasExported}
                        isLoading={isCreating}
                        text="Xuất kho"
                    />
                }
            />
        </>
    );
}; 